import React, { useCallback, useEffect, useRef, useState } from "react";
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useGetOneTemplateQuery, usePutOneTemplateMutation } from "../../../../services/api/repositories/TemplateMailsRepository";
import Moment from "react-moment";
import TypeInput from "../common/form/TypeInput";
import StatusInput from "../common/form/StatusInput";
import DatePicker from "react-datepicker";
import { Alert, Modal } from "react-bootstrap";
import PreviewModal from "../common/PreviewModal";
import { IClientType } from "../../../../interfaces/api/IClientsType";
import ClientInput from "../../mailing-rules/common/form/ClientInput";

export default function TemplateDetailsSection() {
    const navigate = useNavigate();

    const { uuid } = useParams();

    const { data, isLoading, isFetching } = useGetOneTemplateQuery(
        { uuid: uuid || "" },
        { refetchOnMountOrArgChange: true }
    );

    const status = data?.template.status === "active" ? "Actif" : "Inactif";

    const [startDate, setStartDate] = useState<Date | null>();
    const [endDate, setEndDate] = useState<Date | null>();
    const [type, setType] = useState<string>("none");

    const descriptionRef = useRef<HTMLTextAreaElement>(null);
    const statusRef = useRef<HTMLInputElement>(null);
    const subjectRef = useRef<HTMLInputElement>(null);

    const [clientTypeList, setClientTypeList] = useState<IClientType[]>([]);
    const [clientTypeUuidList, setclientTypeUuidList] = useState<string[]>([]);

    const [show, setShow] = useState(false);
    const [errMsg, setErrMsg] = useState<string>("");

    const [showValidation, setShowValidation] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ description: string, type: string, client_types: string, subject: string }>({ description: "", type: "", client_types: "", subject: "" });
    const [isSetByUser, setIsSetByUser] = useState<{ description: boolean, subject: boolean, client_types: boolean }>({ description: false, subject: false, client_types: false });

    const [showPreview, setShowPreview] = useState(false);

    const handleClientTypeAdd = (clientType: IClientType) => {
        if (clientTypeList.some(existingClientType => existingClientType.uuid === clientType.uuid)) {
            return;
        }
        setClientTypeList([...clientTypeList, clientType]);
    };

    const handleClientTypeRemove = (value: string) => {
        setClientTypeList(clientTypeList.filter(item => item.uuid !== value));
        setclientTypeUuidList(clientTypeUuidList.filter(item => item !== value));
    }

    const handleClientTypeUuidAdd = (uuid: string) => {
        if (clientTypeUuidList.some(existingUuidClient => existingUuidClient === uuid)) {
            return;
        }
        setclientTypeUuidList([...clientTypeUuidList, uuid]);
    };

    const handleClosePreview = useCallback(() => {
        setShowPreview(false)
    }, [setShowPreview]);

    const handleShowPreview = useCallback(() => {
        setShowPreview(true);
    }, [setShowPreview]);

    const [putOneTemplateMutation, {
        data: putData,
        isSuccess: isPutSuccess,
        isError: isPutError,
        error: putError
    }] = usePutOneTemplateMutation();

    const handleClose = useCallback(() => {
        setShow(false);
    }, [setShow]);

    const handleShow = useCallback(async () => {
        setShowValidation(true);

        let errors = validationErrors;
        let canSend = true;

        if (descriptionRef.current?.value && descriptionRef.current?.value.length > 255) {
            errors.description = "La description ne doit pas dépasser 255 caractères";
            setIsSetByUser(prevState => ({ ...prevState, description: true }));
            canSend = false;
        }

        if (subjectRef.current?.value.length === 0) {
            errors.subject = "Veuilez saisir un sujet";
            setIsSetByUser(prevState => ({ ...prevState, subject: true }));
            canSend = false;
        }

        if (type === "none") {
            errors.type = "Veuillez saisir un type";
            canSend = false;
        }

        if (clientTypeList.length === 0) {
            errors.client_types = "Veuillez sélectionner au moins un client cible";
            setIsSetByUser(prevState => ({ ...prevState, client_types: true }));
            canSend = false;
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        setShow(true)
    }, [validationErrors, type, clientTypeList.length]);

    const handleSubmit = useCallback(async () => {

        setShowValidation(true);

        if (type === "none"
            || clientTypeList.length === 0
            || !subjectRef.current?.value
            || !statusRef.current?.value) {
            return;
        }

        let errors = validationErrors;
        let canSend = true;

        if (descriptionRef.current?.value && descriptionRef.current?.value.length > 255) {
            errors.description = "La description ne doit pas dépasser 255 caractères";
            setIsSetByUser(prevState => ({ ...prevState, subject: true }));
            canSend = false;
        }

        if (subjectRef.current?.value.length === 0) {
            errors.description = "Le sujet ne doit pas être vide";
            setIsSetByUser(prevState => ({ ...prevState, subject: true }));
            canSend = false;
        }

        if (descriptionRef.current?.value.length === 0) {
            descriptionRef.current.value = "";
        }

        if (type === "none") {
            errors.type = "Un type doit être sélectionné";
            canSend = false;
        }

        if (clientTypeList.length === 0) {
            errors.client_types = "Veuillez sélectionner au moins un client cible";
            canSend = false;
        }

        let start_date = '';
        if (startDate !== null) {
            start_date = `${startDate?.toLocaleDateString()} 00:00:00`;
        } else {
            start_date = `${new Date().toLocaleDateString()} 00:00:00`;
        }

        let end_date = null;
        if (endDate !== null && endDate !== undefined) {
            end_date = `${endDate?.toLocaleDateString()} 23:59:59`;
        }

        let status = 'inactive';
        if (statusRef.current && statusRef.current.checked) {
            status = 'active';
        }

        if (!canSend) {
            setValidationErrors(errors);
            return;
        }

        putOneTemplateMutation({
            uuid: uuid || "",
            data: {
                description: descriptionRef.current ? descriptionRef.current.value : "",
                type: type,
                client_types: clientTypeUuidList,
                subject: subjectRef.current.value,
                status: status,
                date_validity_start: start_date,
                date_validity_end: end_date || null
            }
        });

        setShow(false);

    }, [type, clientTypeList.length, validationErrors, startDate, endDate, putOneTemplateMutation, uuid, clientTypeUuidList]);

    const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        const value = event.target.value;

        if (validationErrors.description && value.length > 0) {
            setValidationErrors({ ...validationErrors, description: "" });
        }

        if (validationErrors.subject && value.length > 0) {
            setValidationErrors({ ...validationErrors, subject: "" });
        }
    };

    const handleError = useCallback(() => {
        if (isPutError && putError !== undefined && putError !== null && typeof putError === 'object' && (errMsg === "" || errMsg === "Une erreur a été rencontrée")) {
            if ("error" in putError) {
                setErrMsg(putError.error as string);
            } else if ("data" in putError && typeof putError.data === 'object' && putError.data !== null && "message" in putError.data) {
                setErrMsg(putError.data.message as string);
            }
        } else if (errMsg === "") {
            setErrMsg("Une erreur a été rencontrée");
        }
    }, [isPutError, putError, errMsg]);

    useEffect(() => {

        if (isPutSuccess) {
            navigate("/templates");
        }

        if (isPutError) {
            handleError();
        }

        if (data === undefined) {
            return;
        }

        if (subjectRef.current !== null && isSetByUser.subject === false) {
            subjectRef.current.value = data.template.subject;
        }

        if (descriptionRef.current !== null && isSetByUser.description === false) {
            descriptionRef.current.value = data.template.description;
        }

        if (descriptionRef.current !== null && descriptionRef.current.value === "") {
            descriptionRef.current.value = data.template.description;
        }

        if (statusRef.current !== null) {
            statusRef.current.value = data.template.status;
        }

        if (!startDate && data.template.date_validity_start !== null) {
            setStartDate(new Date(data.template.date_validity_start));
        }

        if (!endDate && data.template.date_validity_end !== null) {
            setEndDate(new Date(data.template.date_validity_end));
        }

        if (type === "none") {
            setType(data.template.type);
        }

        if (data && data.template && data.template.client_types) {
            setClientTypeList(data.template.client_types);
            setclientTypeUuidList(data.template.client_types.map(clientType => clientType.uuid));
        }

    }, [data, type, startDate, endDate, isPutSuccess, isPutError, navigate, handleError, isSetByUser]);

    return (
        <>
            {(isLoading || isFetching) && <p>Chargement...</p>}

            {((!isLoading && !isFetching) && data !== undefined) && (

                <>
                    <div className="object template" data-status={data.template.status}>
                        <header>
                            <h2>{data.template.subject}</h2>
                            <div className="object-date" title="Date de la dernière mise-à-jour">
                                <span className="icon"></span>
                                <span className="text">
                                    <Moment format="DD/MM/YYYY">
                                        {data.template.updated_at}
                                    </Moment>
                                </span>
                            </div>
                            <div className="object-status" title={`Statut : ${status}`}>
                                <span className="icon"></span>
                                <span className="text">{status}</span>
                            </div>
                            <div className="object-actions">
                                <Button type="button"
                                    variant="btn btn-email-preview"
                                    data-bs-toggle="modal"
                                    data-bs-target="#templatePreviewModal"
                                    title="Prévisualiser le Template email"
                                    onClick={() => { handleShowPreview() }}
                                >
                                    <span className="icon"></span>
                                    <span className="text">Prévisualiser</span>
                                </Button>
                            </div>
                        </header>

                        <Form className="form" onSubmit={handleSubmit} validated={showValidation}>

                            <section className="form-section id">

                                <h3>
                                    <span className="icon"></span>
                                    <span className="text">Définition</span>
                                </h3>

                                <Form.Group className="form-group template-name required">
                                    <Form.Label className="form-label" for="template-name-input">Sujet</Form.Label>
                                    <Form.Control
                                        type="text"
                                        className="form-control w100"
                                        id="template-name-input"
                                        tabIndex={1}
                                        required
                                        ref={subjectRef}
                                        maxLength={255}
                                        onChange={handleChange}
                                        isInvalid={validationErrors.subject.length > 0}
                                    />
                                    <Form.Control.Feedback type="invalid">{validationErrors.subject}</Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="form-group template-description">
                                    <Form.Label className="form-label" htmlFor="template-description-input">Description</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        type="text"
                                        className="form-control w100"
                                        id="template-description-input"
                                        tabIndex={2}
                                        ref={descriptionRef}
                                        maxLength={255}
                                        onChange={handleChange}
                                        isInvalid={validationErrors.description.length > 0}
                                    />
                                    <Form.Control.Feedback type="invalid">{validationErrors.description}</Form.Control.Feedback>
                                </Form.Group>

                                <TypeInput tabIndex={3} setType={setType} type={type} />

                                <StatusInput tabIndex={4} ref={statusRef} />

                                <ClientInput
                                    clientTypeList={clientTypeList}
                                    handleClientTypeAdd={handleClientTypeAdd}
                                    handleClientTypeUuidAdd={handleClientTypeUuidAdd}
                                    handleClientTypeRemove={handleClientTypeRemove}
                                />

                            </section>

                            <section className="form-section schedule">
                                <h3>
                                    <span className="icon" />
                                    <span className="text">Validité</span>
                                </h3>
                                <div className="fromTo">
                                    <Form.Group className="form-group schedule-runDateStart">
                                        <Form.Label className="form-label" htmlFor="template-runDateStart-input">Du</Form.Label>
                                        <DatePicker className="form-control w100" id="template-runDateStart-input" tabIndex={5}
                                            selected={startDate}
                                            dateFormat="dd/MM/yyyy"
                                            onChange={(date) => { setStartDate(date) }} />
                                    </Form.Group>
                                    <Form.Group className="form-group schedule-runDateEnd">
                                        <Form.Label className="form-label" htmlFor="template-runDateEnd-input">Au</Form.Label>
                                        <DatePicker className="form-control w100" id="template-runDateEnd-input" tabIndex={6}
                                            selected={endDate}
                                            dateFormat="dd/MM/yyyy"
                                            onChange={(date) => { setEndDate(date) }} />
                                    </Form.Group>
                                </div>
                            </section>

                            <section className="form-section file">
                                <h3><span className="icon"></span><span className="text">Fichier</span></h3>
                                <Form.Group className="form-group template-file required form-items">
                                    <Form.Label className="form-label" htmlFor="template-file-current-input">Fichier actuel</Form.Label>
                                    <Form.Control
                                        type="text"
                                        className="form-control w100"
                                        id="template-file-current-input"
                                        value={data.template.name}
                                        required
                                        disabled
                                    />
                                </Form.Group >
                            </section>

                            <div>

                                {(validationErrors.client_types) && (
                                    <Alert
                                        variant="danger"
                                        dismissible>
                                        {validationErrors.client_types}
                                    </Alert>
                                )}

                                {isPutError && putData !== undefined && "message" in putData && (
                                    <Alert
                                        variant="danger"
                                        dismissible>
                                        {putData.message}
                                    </Alert>
                                )}

                                {isPutError && errMsg.length > 0
                                    && (
                                        <>
                                            <Alert
                                                variant="danger"
                                                dismissible >
                                                {errMsg}
                                            </Alert>
                                        </>
                                    )}
                            </div>

                            <div className="form-submit">
                                <NavLink to={"/templates"} type="button" className="btn btn-secondary btn-cancel" tabIndex={6} title="Annuler">
                                    <span className="icon"></span>
                                    <span className="text">Annuler</span>
                                </NavLink>
                                <Button type="button" className="btn btn-primary btn-ok" onClick={() => { handleShow() }} tabIndex={5} title="Enregistrer les modifications">
                                    <span className="icon"></span>
                                    <span className="text">Enregistrer</span>
                                </Button>
                            </div>
                        </Form>

                    </div>
                    <PreviewModal show={showPreview} handleClose={handleClosePreview} name={data?.template.name || ''} uuid={uuid || ''} />
                </>
            )}
            <SubmitModal show={show} handleClose={handleClose} handleSubmit={handleSubmit} name={data?.template.name || ''} />

            {((!isLoading && !isFetching) && data === undefined) &&
                <p>Une erreur a été rencontrée.</p>
            }

        </>
    );
}

function SubmitModal({ show, handleClose, handleSubmit, name }: { show: boolean, handleClose: () => void, handleSubmit: () => void, name: string }) {
    return (
        <Modal id="templateSaveChangesModalModal" show={show} onHide={() => { handleClose() }}>
            <Modal.Header closeButton>
                <h1 className="modal-title" id="templateSaveChangesModalLabel">
                    <span className="icon"></span>
                    <span className="text">Enregistrer les modifications</span>
                </h1>
            </Modal.Header>
            <Modal.Body>
                <p>Vous souhaitez enregistrer vos modifications au Template email :</p>
                <div className="object attach" data-status="active">
                    <h2>
                        <span className="icon"></span>
                        <span className="text">{name}</span>
                    </h2>
                </div>
                <p>Veuillez valider les modifications.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    className="btn-cancel"
                    data-bs-dismiss="modal"
                    title="Annuler"
                    onClick={() => { handleClose() }}
                >
                    <span className="icon"></span>
                    Annuler
                </Button>
                <Button
                    variant="primary"
                    className="btn-ok"
                    data-bs-dismiss="modal"
                    title="Valider"
                    onClick={() => { handleSubmit() }}
                >
                    <span className="icon"></span>
                    Valider
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

